import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { PageHeader } from "../../../components/common/PageHeader";
import { TreeIcon } from "../../../components/common/TreeIcon";
import { usePageTitle } from "../../../hooks";

const { Text } = Typography;

/** Stable label used both for the "Mapeo destino" cell render *and*
 *  for its sorter, so the visual order matches the sort. Rows with
 *  no mapping fall to the end (high sentinel). */
const mappingLabel = (row: LegacyType, varieties: Variety[]): string => {
  if (row.existing_species_id) {
    return row.existing_variety_name
      ? `${row.existing_species_name} · ${row.existing_variety_name}`
      : row.existing_species_name ?? "";
  }
  if (row.suggested_variety_id) {
    const v = varieties.find((x) => x.id === row.suggested_variety_id);
    if (v) return `${v.species_name} · ${v.name}`;
  }
  return "￿"; // ensures un-mapped rows sort last
};

interface LegacyType {
  id: number;
  name: string;
  english_name: string;
  usage_count: number;
  migrated_count: number;
  /** Pre-existing mapping when this type has already been migrated
   *  at least once. The backend rejects re-migrations to a different
   *  (species, variety), so the form locks to these values. */
  existing_species_id: string | null;
  existing_species_name: string | null;
  existing_variety_id: string | null;
  existing_variety_name: string | null;
  suggested_variety_id: string | null;
}

interface Species {
  id: string;
  name: string;
  variety_count: number;
}

interface Variety {
  id: string;
  name: string;
  species: string;
  species_name: string;
}

interface MigrateForm {
  species_id: string;
  variety_id?: string;
  variety_name?: string;
}

interface SkippedDetail {
  record_id: number | null;
  audit_id: number | null;
  audit_reference: string | null;
  farm_name: string | null;
  surface: number | null;
  existing_review_id?: string;
  existing_review_comment?: string;
}

interface MigrateResult {
  legacy_type_id: number;
  legacy_type_name: string;
  variety_name: string | null;
  total_legacy_records: number;
  crops_created: number;
  reviews_created: number;
  reviews_skipped_already_migrated: number;
  reviews_skipped_duplicate_in_audit: number;
  records_skipped_no_farm: number;
  records_skipped_no_audit: number;
  skipped_details: {
    no_audit: SkippedDetail[];
    no_farm: SkippedDetail[];
    already_migrated: SkippedDetail[];
    duplicate_in_audit: SkippedDetail[];
  };
  detail_cap_per_reason: number;
}

/**
 * Migration page from the legacy `AuditVarietyNearerType` catalogue
 * to the new `CropSpecies` / `CropVariety` model.
 *
 * The legacy list shows every "tipo de variedad colindante" still
 * in the DB along with how many `AuditVarietyNearer` rows reference
 * it. The admin walks the list, opens the "Migrar" modal on each row,
 * picks (a) a destination `CropSpecies` and (b) either an existing
 * `CropVariety` or a new variety name to create. Submitting converts
 * every legacy usage into the new `AdjacentCrop` + `AdjacentCropReview`
 * rows pointing at the chosen variety. The flow is additive (legacy
 * rows are not deleted) so it's safe to re-run.
 */
export const CropsLegacyMigrationList = () => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();

  const [rows, setRows] = useState<LegacyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [species, setSpecies] = useState<Species[]>([]);
  const [varieties, setVarieties] = useState<Variety[]>([]);

  // Migrate modal state
  const [migrating, setMigrating] = useState(false);
  const [active, setActive] = useState<LegacyType | null>(null);
  const [form] = Form.useForm<MigrateForm>();
  // Result modal — opened after a migrate call completes so we can
  // show the per-bucket breakdown (created / skipped / reasons).
  const [result, setResult] = useState<MigrateResult | null>(null);
  // Watch species_id directly off the form instead of duplicating
  // state — `useState` + `setFieldsValue` runs before the <Form>
  // mounts (destroyOnHidden + {active && ...}), which triggers the
  // "useForm is not connected" warning and breaks the bindings.
  const pickedSpeciesId = Form.useWatch("species_id", form);

  // Search / filter
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "pending" | "migrated">(
    "pending",
  );

  usePageTitle(
    t("crops_legacy.title", "Migración de variedades colindantes antiguas"),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [legacyRes, spRes, vRes] = await Promise.all([
        apiClient.get<LegacyType[]>("/admin/legacy-adjacent-varieties/"),
        apiClient.get<Species[]>("/admin/crop-species/"),
        apiClient.get<Variety[]>("/admin/crop-varieties/"),
      ]);
      setRows(Array.isArray(legacyRes.data) ? legacyRes.data : []);
      setSpecies(Array.isArray(spRes.data) ? spRes.data : []);
      setVarieties(Array.isArray(vRes.data) ? vRes.data : []);
    } catch {
      notify.error(
        t(
          "crops_legacy.load_error",
          "Error al cargar las variedades colindantes antiguas",
        ),
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    return rows.filter((r) => {
      if (filter === "pending" && r.migrated_count >= r.usage_count && r.usage_count > 0)
        return false;
      if (filter === "migrated" && (r.migrated_count === 0 || r.usage_count === 0))
        return false;
      if (q && !r.name.toUpperCase().includes(q) && !r.english_name.toUpperCase().includes(q))
        return false;
      return true;
    });
  }, [rows, filter, search]);

  const varietiesForSpecies = useMemo(() => {
    if (!pickedSpeciesId) return [];
    return varieties.filter((v) => v.species === pickedSpeciesId);
  }, [varieties, pickedSpeciesId]);

  // ----- migrate ----------------------------------------------------

  // Pre-fill the form each time the user opens the modal on a
  // different row. If this row has already been migrated to some
  // (species, variety), force the form to that same target — the
  // backend rejects re-migrations to a different one to keep the
  // collapsed-duplicate records aligned with the originals.
  useEffect(() => {
    if (!active) return;
    form.setFieldsValue({
      species_id: active.existing_species_id ?? undefined,
      variety_id: active.existing_variety_id ?? undefined,
      variety_name: active.existing_variety_name ?? active.name,
    });
  }, [active, form]);

  const openMigrate = (row: LegacyType) => {
    setActive(row);
  };

  const submitMigrate = async () => {
    if (!active) return;
    const values = await form.validateFields();
    setMigrating(true);
    try {
      const res = await apiClient.post<MigrateResult>(
        `/admin/legacy-adjacent-varieties/${active.id}/migrate/`,
        {
          species_id: values.species_id,
          variety_id: values.variety_id || null,
          variety_name: values.variety_name || "",
        },
      );
      const d = res.data;
      const totalSkipped =
        d.reviews_skipped_already_migrated +
        d.reviews_skipped_duplicate_in_audit +
        d.records_skipped_no_farm +
        d.records_skipped_no_audit;
      if (totalSkipped > 0) {
        // Surface a result modal so the admin can see *which* legacy
        // records didn't make it across and why.
        setResult(d);
      } else {
        notify.success(
          t(
            "crops_legacy.migrate_success",
            "{{name}} migrada · {{crops}} cultivos · {{reviews}} revisiones",
            {
              name: active.name,
              crops: d.crops_created,
              reviews: d.reviews_created,
            },
          ),
        );
      }
      setActive(null);
      await load();
    } catch (e: any) {
      const data = e?.response?.data;
      const msg =
        data?.detail ??
        data?.species_id ??
        data?.variety_id ??
        t("crops_legacy.migrate_error", "No se pudo migrar");
      notify.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setMigrating(false);
    }
  };

  // ----- table ------------------------------------------------------

  const columns = [
    {
      title: t("crops_legacy.name", "Nombre legacy"),
      dataIndex: "name",
      key: "name",
      render: (v: string, row: LegacyType) => (
        <div>
          <strong>{v}</strong>
          {row.english_name && row.english_name !== row.name ? (
            <Text type="secondary" style={{ marginLeft: 8 }}>
              · {row.english_name}
            </Text>
          ) : null}
        </div>
      ),
      sorter: (a: LegacyType, b: LegacyType) => a.name.localeCompare(b.name),
    },
    {
      title: t("crops_legacy.usage_count", "Usos legacy"),
      dataIndex: "usage_count",
      key: "usage_count",
      width: 130,
      align: "center" as const,
      render: (n: number) =>
        n > 0 ? <Tag color="blue">{n}</Tag> : <Tag color="default">0</Tag>,
      sorter: (a: LegacyType, b: LegacyType) => a.usage_count - b.usage_count,
      defaultSortOrder: "descend" as const,
    },
    {
      title: t("crops_legacy.migrated_count", "Migrados"),
      dataIndex: "migrated_count",
      key: "migrated_count",
      width: 130,
      align: "center" as const,
      // Sort by completion ratio rather than absolute count — that
      // matches the visual "progress" the cell shows. Ties are
      // broken by absolute migrated count so 5/5 beats 1/1.
      sorter: (a: LegacyType, b: LegacyType) => {
        const ra = a.usage_count > 0 ? a.migrated_count / a.usage_count : -1;
        const rb = b.usage_count > 0 ? b.migrated_count / b.usage_count : -1;
        if (ra !== rb) return ra - rb;
        return a.migrated_count - b.migrated_count;
      },
      render: (n: number, row: LegacyType) => {
        if (row.usage_count === 0) return <Tag color="default">—</Tag>;
        const pct = Math.round((n / row.usage_count) * 100);
        if (n === 0) return <Tag color="orange">{`0 / ${row.usage_count}`}</Tag>;
        if (n >= row.usage_count)
          return (
            <Tag icon={<CheckCircleOutlined />} color="success">
              {n}
            </Tag>
          );
        return (
          <Tag color="processing">
            {n}/{row.usage_count} ({pct}%)
          </Tag>
        );
      },
    },
    {
      title: t("crops_legacy.suggestion", "Mapeo destino"),
      key: "suggestion",
      // Sort by the same label we render, so the user can group
      // rows that already point at the same species/variety.
      sorter: (a: LegacyType, b: LegacyType) =>
        mappingLabel(a, varieties).localeCompare(mappingLabel(b, varieties)),
      render: (_: any, row: LegacyType) => {
        // Already-migrated rows show their locked target — that's
        // the most useful piece of info on a re-visit.
        if (row.existing_species_id) {
          const label = row.existing_variety_name
            ? `${row.existing_species_name} · ${row.existing_variety_name}`
            : row.existing_species_name ?? "";
          return <Tag color="purple">{label}</Tag>;
        }
        if (!row.suggested_variety_id) {
          return (
            <Text type="secondary">
              {t("crops_legacy.no_match", "sin coincidencia")}
            </Text>
          );
        }
        const v = varieties.find((x) => x.id === row.suggested_variety_id);
        if (!v) return <Text type="secondary">—</Text>;
        return (
          <Tag color="green">
            {v.species_name} · {v.name}
          </Tag>
        );
      },
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 160,
      align: "right" as const,
      render: (_: any, row: LegacyType) => (
        <Button
          icon={<SwapOutlined />}
          size="small"
          type={row.migrated_count >= row.usage_count ? "default" : "primary"}
          onClick={() => openMigrate(row)}
        >
          {row.migrated_count > 0 && row.migrated_count < row.usage_count
            ? t("crops_legacy.resume", "Reintentar")
            : row.migrated_count >= row.usage_count && row.usage_count > 0
              ? t("crops_legacy.remigrate", "Re-migrar")
              : t("crops_legacy.migrate", "Migrar")}
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<TreeIcon />}
        accent="#FA8C16"
        title={t(
          "crops_legacy.title",
          "Migración de variedades colindantes antiguas",
        )}
        subtitle={t(
          "crops_legacy.subtitle",
          "Reasigna los tipos de la lista legacy (AuditVarietyNearerType) a especies y variedades del nuevo catálogo. Cada migración crea los registros AdjacentCrop/Review correspondientes — los datos antiguos se conservan para auditoría.",
        )}
        extra={
          <Space wrap>
            <Link to="/admin/crops">
              <Button icon={<ArrowLeftOutlined />}>
                {t("crops_legacy.back", "Volver a Especies")}
              </Button>
            </Link>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              {t("common.reload", "Refrescar")}
            </Button>
          </Space>
        }
      />

      <Alert
        showIcon
        type="info"
        style={{ marginBottom: 12 }}
        message={t(
          "crops_legacy.banner",
          "Cómo funciona: pulsa Migrar en un tipo legacy → elige la especie destino → escribe (o selecciona) la variedad nueva. Todas las variedades colindantes que usaban ese tipo legacy se replican en el modelo nuevo apuntando a la variedad elegida. Es idempotente: puedes re-ejecutar sin duplicar.",
        )}
      />

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          placeholder={t("crops_legacy.search_placeholder", "Buscar por nombre…")}
          allowClear
          style={{ width: 260 }}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
        <Select
          value={filter}
          onChange={(v) => setFilter(v)}
          style={{ width: 200 }}
          options={[
            { value: "pending", label: t("crops_legacy.filter_pending", "Pendientes") },
            { value: "migrated", label: t("crops_legacy.filter_migrated", "Ya migrados") },
            { value: "all", label: t("crops_legacy.filter_all", "Todos") },
          ]}
        />
        <Text type="secondary">
          {t("crops_legacy.summary", "{{n}} tipos · {{usage}} usos en total", {
            n: filtered.length,
            usage: filtered.reduce((s, r) => s + r.usage_count, 0),
          })}
        </Text>
      </Space>

      <Table<LegacyType>
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 900 }}
        sortDirections={["ascend", "descend", "ascend"]}
        showSorterTooltip={false}
      />

      <Modal
        title={
          active
            ? t("crops_legacy.modal_title", "Migrar «{{name}}»", {
                name: active.name,
              })
            : t("crops_legacy.modal_title_blank", "Migrar")
        }
        open={!!active}
        onCancel={() => (migrating ? undefined : setActive(null))}
        onOk={submitMigrate}
        confirmLoading={migrating}
        okText={t("crops_legacy.modal_ok", "Migrar")}
        cancelText={t("common.cancel", "Cancelar")}
        okButtonProps={{ icon: <SwapOutlined /> }}
        width={600}
        // Reset form when the modal finishes closing so the next
        // open starts fresh — without conditional rendering of the
        // <Form>, which would unbind the useForm instance and break
        // the dropdown popups.
        afterOpenChange={(open) => {
          if (!open) {
            form.resetFields();
          }
        }}
      >
        <Alert
          showIcon
          type="warning"
          style={{ marginBottom: 16 }}
          message={t(
            "crops_legacy.modal_info",
            "Se migrarán {{n}} registros legacy. Por cada uno se creará (o reutilizará) un AdjacentCrop en la finca correspondiente y una AdjacentCropReview en su auditoría.",
            { n: active?.usage_count ?? 0 },
          )}
        />

        {active?.existing_species_id ? (
          <Alert
            showIcon
            type="info"
            style={{ marginBottom: 16 }}
            message={t(
              "crops_legacy.existing_target",
              "Esta variedad ya está parcialmente migrada a «{{species}}{{varietySep}}{{variety}}». Sólo se completarán los registros que faltan — el destino está bloqueado para mantener consistencia.",
              {
                species: active.existing_species_name ?? "",
                variety: active.existing_variety_name ?? "",
                varietySep: active.existing_variety_name ? " · " : "",
              },
            )}
          />
        ) : null}
        <Form
          form={form}
          layout="vertical"
          // initialValues runs only on first mount, so seed the
          // variety_name from `active` each time it changes using
          // a separate effect (see `openMigrate`).
        >
              <Form.Item
                label={t("crops_legacy.species_label", "Especie destino")}
                name="species_id"
                rules={[
                  {
                    required: true,
                    message: t(
                      "crops_legacy.species_required",
                      "Elige una especie",
                    ),
                  },
                ]}
                extra={t(
                  "crops_legacy.species_hint",
                  "¿No existe la especie? Créala en /admin/crops antes de migrar.",
                )}
              >
                <Select
                  placeholder={t(
                    "crops_legacy.species_placeholder",
                    "Selecciona la especie en el catálogo nuevo",
                  )}
                  showSearch
                  optionFilterProp="label"
                  options={species.map((s) => ({
                    value: s.id,
                    label: s.name,
                  }))}
                  disabled={!!active?.existing_species_id}
                  onChange={() => {
                    // Clear any previously chosen variety so it
                    // doesn't end up belonging to a different species
                    // after switching the parent.
                    form.setFieldsValue({ variety_id: undefined });
                  }}
                  getPopupContainer={(node) =>
                    (node?.parentElement as HTMLElement) ?? document.body
                  }
                />
              </Form.Item>

              <Form.Item
                label={t(
                  "crops_legacy.variety_existing_label",
                  "Variedad existente (opcional)",
                )}
                name="variety_id"
                extra={t(
                  "crops_legacy.variety_existing_hint",
                  "Si la variedad ya existe bajo la especie elegida, selecciónala aquí. Deja vacío para crearla con el nombre de abajo.",
                )}
              >
                <Select
                  placeholder={
                    pickedSpeciesId
                      ? t(
                          "crops_legacy.variety_existing_placeholder",
                          "Reutilizar una variedad existente",
                        )
                      : t(
                          "crops_legacy.variety_existing_disabled",
                          "Elige primero la especie",
                        )
                  }
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  disabled={
                    !pickedSpeciesId || !!active?.existing_variety_id
                  }
                  options={varietiesForSpecies.map((v) => ({
                    value: v.id,
                    label: v.name,
                  }))}
                  getPopupContainer={(node) =>
                    (node?.parentElement as HTMLElement) ?? document.body
                  }
                />
              </Form.Item>

              <Form.Item
                label={t(
                  "crops_legacy.variety_new_label",
                  "Nombre de la variedad a crear",
                )}
                name="variety_name"
                extra={t(
                  "crops_legacy.variety_new_hint",
                  "Si arriba no eliges una existente, se creará una variedad con este nombre bajo la especie. Déjalo vacío para mapear el tipo legacy solo a la especie (sin variedad).",
                )}
              >
                <Input
                  placeholder={active?.name ?? ""}
                  maxLength={100}
                  disabled={!!active?.existing_variety_id}
                />
              </Form.Item>
            </Form>
      </Modal>

      <ResultModal result={result} onClose={() => setResult(null)} />
    </Card>
  );
};

// ----- result modal --------------------------------------------------

const ResultModal: React.FC<{
  result: MigrateResult | null;
  onClose: () => void;
}> = ({ result, onClose }) => {
  const { t } = useTranslation();
  if (!result) return null;

  const totalSkipped =
    result.reviews_skipped_already_migrated +
    result.reviews_skipped_duplicate_in_audit +
    result.records_skipped_no_farm +
    result.records_skipped_no_audit;

  const buckets: Array<{
    key: keyof MigrateResult["skipped_details"];
    label: string;
    color: string;
    count: number;
    help: string;
  }> = [
    {
      key: "duplicate_in_audit",
      label: t(
        "crops_legacy.skip_duplicate",
        "Misma auditoría ya tenía otra revisión del mismo cultivo",
      ),
      color: "orange",
      count: result.reviews_skipped_duplicate_in_audit,
      help: t(
        "crops_legacy.skip_duplicate_help",
        "Dos o más registros legacy apuntaban a la misma (finca, auditoría) — el modelo nuevo solo admite una revisión por cultivo y auditoría, así que se han colapsado.",
      ),
    },
    {
      key: "already_migrated",
      label: t("crops_legacy.skip_migrated", "Ya migrados previamente"),
      color: "blue",
      count: result.reviews_skipped_already_migrated,
      help: t(
        "crops_legacy.skip_migrated_help",
        "Se detectó la marca de migración previa en la revisión existente. Es seguro: el resultado del modelo nuevo ya estaba ahí.",
      ),
    },
    {
      key: "no_farm",
      label: t("crops_legacy.skip_no_farm", "Auditoría sin finca asignada"),
      color: "red",
      count: result.records_skipped_no_farm,
      help: t(
        "crops_legacy.skip_no_farm_help",
        "La auditoría asociada no tiene finca — no podemos crear un AdjacentCrop sin finca. Revisa esas auditorías a mano.",
      ),
    },
    {
      key: "no_audit",
      label: t("crops_legacy.skip_no_audit", "Sin auditoría asociada"),
      color: "red",
      count: result.records_skipped_no_audit,
      help: t(
        "crops_legacy.skip_no_audit_help",
        "El registro legacy no tiene auditoría — caso inusual, revísalo manualmente.",
      ),
    },
  ];

  const detailColumns = [
    {
      title: t("crops_legacy.detail_record", "Registro legacy"),
      dataIndex: "record_id",
      key: "record_id",
      width: 110,
      render: (v: number | null) => (v != null ? `#${v}` : "—"),
    },
    {
      title: t("crops_legacy.detail_audit", "Auditoría"),
      dataIndex: "audit_reference",
      key: "audit_reference",
      render: (v: string | null, row: SkippedDetail) =>
        v ? (
          <>
            <strong>{v}</strong>
            {row.audit_id ? (
              <Text type="secondary" style={{ marginLeft: 6 }}>
                (#{row.audit_id})
              </Text>
            ) : null}
          </>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t("crops_legacy.detail_farm", "Finca"),
      dataIndex: "farm_name",
      key: "farm_name",
      render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: t("crops_legacy.detail_surface", "Superficie"),
      dataIndex: "surface",
      key: "surface",
      width: 110,
      align: "right" as const,
      render: (v: number | null) =>
        v != null ? `${v.toFixed(2)} ha` : "—",
    },
  ];

  return (
    <Modal
      title={t("crops_legacy.result_title", "Resultado de la migración: «{{name}}»", {
        name: result.legacy_type_name,
      })}
      open={!!result}
      onCancel={onClose}
      onOk={onClose}
      okText={t("common.ok", "Aceptar")}
      cancelButtonProps={{ style: { display: "none" } }}
      width={780}
    >
      <Alert
        showIcon
        type={totalSkipped === 0 ? "success" : "warning"}
        style={{ marginBottom: 16 }}
        message={t(
          "crops_legacy.result_summary",
          "{{migrated}} de {{total}} registros migrados ({{pct}}%). {{skipped}} omitidos.",
          {
            migrated: result.reviews_created + result.reviews_skipped_already_migrated,
            total: result.total_legacy_records,
            pct: result.total_legacy_records
              ? Math.round(
                  ((result.reviews_created +
                    result.reviews_skipped_already_migrated) /
                    result.total_legacy_records) *
                    100,
                )
              : 0,
            skipped: totalSkipped,
          },
        )}
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Tag color="green">
          {t("crops_legacy.stat_crops", "Cultivos creados")}: {result.crops_created}
        </Tag>
        <Tag color="green">
          {t("crops_legacy.stat_reviews", "Revisiones nuevas")}:{" "}
          {result.reviews_created}
        </Tag>
        {buckets.map((b) =>
          b.count > 0 ? (
            <Tag color={b.color} key={b.key}>
              {b.label}: {b.count}
            </Tag>
          ) : null,
        )}
      </Space>

      {buckets
        .filter((b) => b.count > 0)
        .map((b) => {
          const rows = result.skipped_details[b.key];
          return (
            <div key={b.key} style={{ marginBottom: 16 }}>
              <Text strong>
                {b.label} ({b.count})
              </Text>
              <Text
                type="secondary"
                style={{ display: "block", marginBottom: 8, fontSize: 12 }}
              >
                {b.help}
                {b.count > result.detail_cap_per_reason && (
                  <>
                    {" "}
                    {t(
                      "crops_legacy.detail_truncated",
                      "Mostrando los primeros {{cap}} — el resto sigue el mismo patrón.",
                      { cap: result.detail_cap_per_reason },
                    )}
                  </>
                )}
              </Text>
              <Table<SkippedDetail>
                dataSource={rows}
                columns={detailColumns}
                rowKey={(r) => `${b.key}-${r.record_id}`}
                size="small"
                pagination={{ pageSize: 10, showSizeChanger: false }}
              />
            </div>
          );
        })}
    </Modal>
  );
};

export default CropsLegacyMigrationList;
